import { Alert, Form, Input, Radio, Space, Spin, Tag, Typography } from 'antd'
import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'

import {
  ordersListFulfillmentOrderBuyerDeliveryAddressesOptions,
  type DeliveryAddressRoot,
} from '@repo/sdk'

import { useChangeShippingAddress } from './useChangeShippingAddress'
import { Popup } from '@/components/popup'

interface ChangeShippingAddressModalProps {
  fulfillmentOrderId: string
  /** Address the parcel ships to now; null when the buyer already deleted it (#87). */
  currentAddressId: string | null
  open: boolean
  onClose: () => void
}

interface FormValues {
  addressId: string
  reason?: string
}

function formatAddressLine(address: DeliveryAddressRoot): string {
  const line2 = address.addressLine2?.trim()
  return `(${address.postalCode}) ${address.addressLine1}${line2 ? ` ${line2}` : ''}`
}

/**
 * #87: the dealer moves their parcel to another address the buyer registered. The list comes from
 * the server scoped by this fulfillment order — no free-text address, no browsing other buyers.
 */
export function ChangeShippingAddressModal({
  fulfillmentOrderId,
  currentAddressId,
  open,
  onClose,
}: ChangeShippingAddressModalProps) {
  const { t } = useTranslation()
  const [form] = Form.useForm<FormValues>()
  const { changeShippingAddress, isChanging } = useChangeShippingAddress()

  const addressesQuery = useQuery({
    ...ordersListFulfillmentOrderBuyerDeliveryAddressesOptions({ path: { fulfillmentOrderId } }),
    enabled: open,
  })
  const addresses: DeliveryAddressRoot[] = addressesQuery.data?.data ?? []
  const selectable = addresses.filter((address) => address.addressId !== currentAddressId)

  const handleOk = () => {
    void form.validateFields().then((values) => {
      changeShippingAddress({
        fulfillmentOrderId,
        addressId: values.addressId,
        reason: values.reason?.trim() || undefined,
        onDone: () => {
          form.resetFields()
          onClose()
        },
      })
    })
  }

  const handleCancel = () => {
    form.resetFields()
    onClose()
  }

  return (
    <Popup
      title={t('pages.orders.changeShippingAddress.title')}
      open={open}
      onOk={handleOk}
      onCancel={handleCancel}
      confirmLoading={isChanging}
      okText={t('common.buttons.save')}
      cancelText={t('common.buttons.cancel')}
      okButtonProps={{
        disabled: selectable.length === 0,
        'data-testid': 'change-shipping-address-ok-btn',
      }}
    >
      <Typography.Paragraph type="secondary">
        {t('pages.orders.changeShippingAddress.description')}
      </Typography.Paragraph>
      {addressesQuery.isLoading ? (
        <Spin />
      ) : addressesQuery.isError ? (
        <Alert type="error" showIcon message={t('pages.orders.changeShippingAddress.loadError')} />
      ) : selectable.length === 0 ? (
        <Alert
          type="warning"
          showIcon
          message={t('pages.orders.changeShippingAddress.noAddresses')}
        />
      ) : (
        <Form form={form} layout="vertical">
          <Form.Item
            name="addressId"
            label={t('pages.orders.changeShippingAddress.selectLabel')}
            rules={[
              { required: true, message: t('pages.orders.changeShippingAddress.selectRequired') },
            ]}
          >
            <Radio.Group style={{ width: '100%' }} data-testid="change-shipping-address-options">
              <Space direction="vertical" style={{ width: '100%' }}>
                {selectable.map((address) => (
                  <Radio key={address.addressId} value={address.addressId}>
                    <Space direction="vertical" size={0}>
                      <Space size={4}>
                        <Typography.Text strong>{address.recipientName}</Typography.Text>
                        <Typography.Text type="secondary">{address.phoneNumber}</Typography.Text>
                        {address.isDefault && (
                          <Tag color="blue">
                            {t('pages.orders.changeShippingAddress.defaultTag')}
                          </Tag>
                        )}
                      </Space>
                      <Typography.Text>{formatAddressLine(address)}</Typography.Text>
                    </Space>
                  </Radio>
                ))}
              </Space>
            </Radio.Group>
          </Form.Item>
          <Form.Item name="reason" label={t('pages.orders.changeShippingAddress.reasonLabel')}>
            <Input
              maxLength={200}
              placeholder={t('pages.orders.changeShippingAddress.reasonPlaceholder')}
            />
          </Form.Item>
        </Form>
      )}
    </Popup>
  )
}
